import axios from "axios";
import React, { useRef, useState } from "react";
import { IconContext } from "react-icons";
import { IoEyeOffSharp, IoEyeSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LogoKemenag from "assets/img/logo-kemenag.png";

/* =====================================================
   TOGGLE SWITCH
===================================================== */
function ToggleSwitch({ isChecked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isChecked}
      onClick={onChange}
      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 focus:outline-none ${
        isChecked ? "bg-green-600" : "bg-[#eeeeee]"
      }`}
    >
      <span
        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-200 ${
          isChecked ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </button>
  );
}

/* =====================================================
   LOGIN COMPONENT
===================================================== */
function Login({ closePopup, onLoginSuccess }) {
  const navigate = useNavigate();

  const popupRef = useRef(null);

  /* =====================================================
     REMEMBER LOGIN
  ===================================================== */
  const rememberedEmail = localStorage.getItem("rememberedEmail") || "";

  const rememberedPassword = localStorage.getItem("rememberedPassword") || "";

  /* =====================================================
     STATE
  ===================================================== */
  const [email, setEmail] = useState(rememberedEmail);

  const [password, setPassword] = useState(rememberedPassword);

  const [showPassword, setShowPassword] = useState(false);

  const [isChecked, setIsChecked] = useState(
    !!(rememberedEmail && rememberedPassword)
  );

  const [emailError, setEmailError] = useState(false);

  const [passwordError, setPasswordError] = useState(false);

  const [loading, setLoading] = useState(false);

  /*
   * true  = Login Geschool
   * false = Login Dashboard Admin
   */
  const [useFetch, setUseFetch] = useState(true);

  /* =====================================================
     CLICK OUTSIDE
  ===================================================== */
  const handleOutsideClick = (e) => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      closePopup();
    }
  };

  /* =====================================================
     REMEMBER ME
  ===================================================== */
  const handleCheckboxChange = () => {
    const newValue = !isChecked;

    setIsChecked(newValue);

    if (!newValue) {
      localStorage.removeItem("rememberedEmail");

      localStorage.removeItem("rememberedPassword");
    }
  };

  /* =====================================================
     HANDLE LOGIN
  ===================================================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    setEmailError(false);
    setPasswordError(false);

    /* ================================
       VALIDATION
    ================================= */
    if (!email && !password) {
      setEmailError(true);
      setPasswordError(true);
      setLoading(false);
      return;
    }

    if (!email) {
      setEmailError(true);
      setLoading(false);
      return;
    }

    if (!password) {
      setPasswordError(true);
      setLoading(false);
      return;
    }

    const data = {
      email,
      password,
      isChecked,
    };

    /* =====================================================
       LOGIN GESCHOOL
    ===================================================== */
    if (useFetch) {
      fetch("/_api/main/login", {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify(data),
      })
        .then((res) => {
          return res.json();
        })

        .then((result) => {
          if (result.success) {
            window.location.href = result.redirect_uri;

            return;
          }

          if (result.error) {
            setPasswordError(true);
            setEmailError(true);
          }
        })

        .catch(() => {
          setPasswordError(true);
          setEmailError(true);
        })

        .finally(() => {
          setLoading(false);
        });
    } else {
      /* =====================================================
       LOGIN DASHBOARD ADMIN
    ===================================================== */
      const loginData = JSON.stringify({
        username: email,
        password: password,
      });

      const config = {
        method: "post",

        maxBodyLength: Infinity,

        url: `/api_w/users/login`,

        headers: {
          "Content-Type": "application/json",

          db_name: process.env.REACT_APP_DB_NAME,
        },

        data: loginData,
      };

      try {
        const response = await axios.request(config);

        if (response.status === 200) {
          const { token, user } = response.data;

          /* ================================
             SIMPAN LOGIN
          ================================= */
          localStorage.setItem("token", token);

          localStorage.setItem("id", user.id);

          localStorage.setItem("role", user.role);

          /* ================================
             REMEMBER LOGIN
          ================================= */
          if (isChecked) {
            localStorage.setItem("rememberedEmail", email);

            localStorage.setItem("rememberedPassword", password);
          } else {
            localStorage.removeItem("rememberedEmail");

            localStorage.removeItem("rememberedPassword");
          }

          toast.success("Berhasil Login");

          /* ================================
             REDIRECT
          ================================= */
          if (user.role === "superadmin" || user.role === "admin") {
            navigate("/admin");
          } else {
            navigate("/");

            if (onLoginSuccess) {
              onLoginSuccess();
            }
          }
        } else {
          toast.error("Gagal Login");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  /* =====================================================
     LOGIN UI
  ===================================================== */
  return (
    <div
      onClick={handleOutsideClick}
      className="
        fixed
        inset-0
        z-50
        flex
        items-center
        justify-center
        bg-gray-900
        bg-opacity-50
        px-4
      "
    >
      {/* =====================================================
          FORM LOGIN
      ===================================================== */}
      <form
        ref={popupRef}
        onSubmit={handleSubmit}
        className="
          text-black
          relative
          flex
          w-[500px]
          max-w-[calc(100vw-32px)]
          flex-col
          rounded-[24px]
          bg-white
          px-6
          py-6
          text-left
          font-light
          shadow-lg
        "
      >
        {/* =====================================================
            LOGO
        ===================================================== */}
        <img
          src={LogoKemenag}
          alt="Kemenag Logo"
          className="
            mx-auto
            mb-2
            h-24
            w-auto
            object-contain
          "
        />

        {/* =====================================================
            TITLE
        ===================================================== */}
        <div
          className="
            flex
            w-full
            flex-col
            gap-1
            text-left
          "
        >
          <div
            className="
              text-5xl
              font-bold
              leading-tight
              text-[#000]
            "
          >
            Masuk
          </div>

          <div
            className="
              text-lg
              font-normal
              text-[#000]
            "
          >
            Silakan masuk menggunakan akun Anda
          </div>
        </div>

        <div className="mt-6 w-full text-left">
          <label
            htmlFor="email"
            className="
              mb-2
              block
              w-full
              text-left
              text-base
              font-medium
              text-[#333333]
            "
          >
            Username / Email
          </label>

          <div
            className={`
    relative
    flex
    h-16
    w-full
    items-center
    rounded-lg
    border
    border-[#dedede]
    bg-white
    px-4
    focus-within:ring-2
    focus-within:ring-[#00520A]

    ${emailError ? "ring-2 ring-red-500" : ""}
  `}
          >
            <input
              id="email"
              type="text"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              aria-invalid={emailError}
              placeholder="Masukkan email / username"
              className="
                bg-transparent
                h-full
                w-full
                text-left
                text-base
                text-[#222222]
                outline-none
                placeholder:text-[#b8b8b8]
              "
            />
          </div>

          {emailError && (
            <div
              className="
                mt-1
                text-left
                text-sm
                text-red-500
              "
            >
              Email / username tidak sesuai
            </div>
          )}
        </div>

        <div className="mt-5 w-full text-left">
          <label
            htmlFor="password"
            className="
              mb-2
              block
              w-full
              text-left
              text-base
              font-medium
              text-[#333333]
            "
          >
            Password
          </label>

          <div
            className={`
    relative
    flex
    h-16
    w-full
    items-center
    rounded-lg
    border
    border-[#dedede]
    bg-white
    px-4
    focus-within:ring-2
    focus-within:ring-[#00520A]

    ${passwordError ? "ring-2 ring-red-500" : ""}
  `}
          >
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              aria-invalid={passwordError}
              placeholder="Masukkan password"
              className="
                bg-transparent
                h-full
                w-full
                text-left
                text-base
                text-[#222222]
                outline-none
                placeholder:text-[#b8b8b8]
              "
            />

            {/* EYE ICON */}
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={
                showPassword ? "Sembunyikan password" : "Tampilkan password"
              }
              className="
                absolute
                right-3
                top-1/2
                -translate-y-1/2
                text-[#555555]
              "
            >
              <IconContext.Provider
                value={{
                  size: "21px",
                }}
              >
                {showPassword ? <IoEyeOffSharp /> : <IoEyeSharp />}
              </IconContext.Provider>
            </button>
          </div>

          {passwordError && (
            <div
              className="
                mt-1
                text-left
                text-sm
                text-red-500
              "
            >
              Password tidak sesuai
            </div>
          )}
        </div>

        <div
          className="
            mt-5
            flex
            items-center
            justify-start
            text-left
          "
        >
          <div
            className="
              flex
              items-center
              gap-3
            "
          >
            <ToggleSwitch
              isChecked={isChecked}
              onChange={handleCheckboxChange}
            />

            <div
              className="
                text-left
                text-base
                text-[#000000]
              "
            >
              Ingat Saya
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="
            mt-8
            flex
            h-14
            w-full
            items-center
            justify-center
            rounded-lg
            bg-[#00520A]
            text-xl
            font-semibold
            text-white
            transition
            hover:bg-[#004408]
            disabled:cursor-not-allowed
            disabled:opacity-60
          "
        >
          {loading ? "Loading..." : "Masuk"}
        </button>

        <div
          className="
            mt-6
            text-center
            text-base
            leading-[1.4]
            text-[#131313]
          "
        >
          Tidak memiliki akun?
          <br />
          Silakan hubungi Admin Madrasah
        </div>
        <div
          className="
            mt-3
            text-center
          "
        >
          <button
            type="button"
            onClick={() => setUseFetch(!useFetch)}
            className="
              text-green-700
            "
          >
            {useFetch
              ? "Switch to Login Dashboard"
              : "Switch to Login Geschool"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Login;
